/**
 * ResultSearchPage 컴포넌트
 * 채점 결과 조회 페이지
 * - 문제집 선택
 * - 페이지 번호 입력
 * - 결과 조회
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { PixelButton } from '../components/common/PixelButton';
import { PixelCard } from '../components/common/PixelCard';
import { PixelInput } from '../components/common/PixelInput';
import { AssignedWorkbook } from '../types/workbook';
import { getMyAssignments } from '../services/workbookService';

/**
 * ResultSearchPage 컴포넌트
 */
export const ResultSearchPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  // 상태 관리
  const [assignments, setAssignments] = useState<AssignedWorkbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorkbook, setSelectedWorkbook] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<string>('');

  /**
   * 배정된 문제집 목록 조회
   */
  useEffect(() => {
    const fetchAssignments = async () => {
      try {
        setLoading(true);

        if (!user?.id) {
          throw new Error('사용자 정보가 없습니다');
        }

        const response = await getMyAssignments(user.id);
        setAssignments(response.assignments);

        // 첫 번째 문제집 자동 선택
        if (response.assignments.length > 0) {
          setSelectedWorkbook(response.assignments[0].workbook_name);
        }
      } catch (err: any) {
        console.error('Failed to fetch assignments:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [user]);

  /**
   * 조회 핸들러
   */
  const handleSearch = () => {
    // 입력 검증
    if (!selectedWorkbook) {
      alert('문제집을 선택해주세요');
      return;
    }

    if (!pageNumber || isNaN(parseInt(pageNumber)) || parseInt(pageNumber) <= 0) {
      alert('올바른 페이지 번호를 입력해주세요');
      return;
    }

    // 결과 페이지로 이동
    navigate(`/workbooks/${encodeURIComponent(selectedWorkbook)}/page/${pageNumber}/result`);
  };

  /**
   * 홈으로 돌아가기
   */
  const handleGoHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black p-4 flex items-center justify-center">
      <div className="max-w-md w-full">
        <PixelCard variant="panel" padding="large">
          {/* 헤더 */}
          <h2 className="font-pixel text-xl text-[#9bbc0f] mb-6 text-center">
            📊 채점 결과 조회
          </h2>

          {/* 로딩 상태 */}
          {loading && (
            <div className="text-center py-8">
              <p className="font-pixel text-sm text-gray-400 animate-pulse">
                로딩 중...
              </p>
            </div>
          )}

          {/* 입력 폼 */}
          {!loading && assignments.length > 0 && (
            <div className="space-y-4">
              {/* 문제집 선택 */}
              <div>
                <label className="font-pixel text-sm text-gray-400 mb-2 block">
                  문제집 선택
                </label>
                <select
                  value={selectedWorkbook}
                  onChange={(e) => setSelectedWorkbook(e.target.value)}
                  className="w-full bg-gray-900 border-2 border-gray-700 text-white font-pixel text-sm p-3 rounded-sm focus:outline-none focus:border-[#9bbc0f]"
                >
                  {assignments.map((assignment) => (
                    <option
                      key={assignment.workbook_name}
                      value={assignment.workbook_name}
                    >
                      {assignment.workbook_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* 페이지 번호 입력 */}
              <div>
                <label className="font-pixel text-sm text-gray-400 mb-2 block">
                  페이지 번호
                </label>
                <PixelInput
                  type="number"
                  placeholder="페이지 번호 입력"
                  value={pageNumber}
                  onChange={(e) => setPageNumber(e.target.value)}
                  min="1"
                />
              </div>

              {/* 버튼 */}
              <div className="space-y-3 pt-4">
                <PixelButton
                  variant="primary"
                  size="medium"
                  onClick={handleSearch}
                  fullWidth
                >
                  조회
                </PixelButton>

                <PixelButton
                  variant="secondary"
                  size="medium"
                  onClick={handleGoHome}
                  fullWidth
                >
                  홈으로
                </PixelButton>
              </div>
            </div>
          )}

          {/* 빈 상태 (배정된 문제집 없음) */}
          {!loading && assignments.length === 0 && (
            <div className="text-center py-8">
              <p className="font-pixel text-sm text-gray-400 mb-4">
                배정된 문제집이 없습니다
              </p>
              <PixelButton
                variant="secondary"
                size="small"
                onClick={handleGoHome}
              >
                홈으로
              </PixelButton>
            </div>
          )}
        </PixelCard>
      </div>
    </div>
  );
};
